import { useEffect } from 'react';

/**
 * Public preview gallery showing 6 different animated clapping treatments
 * to replace the GOAL! badge on the success page. Pick one and we'll wire it in.
 */
export default function ClapPreview() {
  // Force animations to restart when navigating in
  useEffect(() => {
    const t = setTimeout(() => {
      document.querySelectorAll('.clap-tile').forEach(el => {
        el.classList.remove('clap-tile-replay');
        // Force reflow then re-add class to restart anims
        void el.offsetWidth;
        el.classList.add('clap-tile-replay');
      });
    }, 100);
    return () => clearTimeout(t);
  }, []);

  const variants = [
    {
      id: 'v1',
      title: 'Variant 1 · Big Bounce',
      subtitle: 'Emoji pulses & bounces on impact',
      element: <span className="clap-v1">👏</span>,
    },
    {
      id: 'v2',
      title: 'Variant 2 · Clap Shake',
      subtitle: 'Side-to-side clap with motion blur',
      element: <span className="clap-v2">👏</span>,
    },
    {
      id: 'v3',
      title: 'Variant 3 · Burst Rays',
      subtitle: 'Brand-green rays radiate behind',
      element: (
        <span className="clap-v3-wrap">
          <span className="clap-v3-rays" aria-hidden />
          <span className="clap-v3-emoji">👏</span>
        </span>
      ),
    },
    {
      id: 'v4',
      title: 'Variant 4 · Triple Cascade',
      subtitle: 'Three claps stagger in',
      element: (
        <span className="clap-v4-wrap">
          <span className="clap-v4-a">👏</span>
          <span className="clap-v4-b">👏</span>
          <span className="clap-v4-c">👏</span>
        </span>
      ),
    },
    {
      id: 'v5',
      title: 'Variant 5 · Halo Pulse',
      subtitle: 'Emerald halo ring pulses on every clap',
      element: (
        <span className="clap-v5-wrap">
          <span className="clap-v5-halo" aria-hidden />
          <span className="clap-v5-halo clap-v5-halo-2" aria-hidden />
          <span className="clap-v5-emoji">👏</span>
        </span>
      ),
    },
    {
      id: 'v6',
      title: 'Variant 6 · Tilted + Motion Dashes',
      subtitle: 'Grey dashes pop out on every clap — like the emoji itself',
      element: (
        <span className="clap-v6-wrap">
          <span className="clap-v6-emoji">👏</span>
          {/* Top-right motion dashes */}
          <span className="clap-v6-dash clap-v6-tr1" />
          <span className="clap-v6-dash clap-v6-tr2" />
          <span className="clap-v6-dash clap-v6-tr3" />
          <span className="clap-v6-dash clap-v6-tr4" />
          {/* Bottom-left motion dashes */}
          <span className="clap-v6-dash clap-v6-bl1" />
          <span className="clap-v6-dash clap-v6-bl2" />
          <span className="clap-v6-dash clap-v6-bl3" />
          <span className="clap-v6-dash clap-v6-bl4" />
        </span>
      ),
    },
  ];

  const replayAll = () => {
    document.querySelectorAll('.clap-tile').forEach(el => {
      el.classList.remove('clap-tile-replay');
      void el.offsetWidth;
      el.classList.add('clap-tile-replay');
    });
  };

  return (
    <div
      className="min-h-screen px-6 py-12"
      style={{
        background: 'radial-gradient(ellipse at 50% 0%, rgba(0,122,51,0.15) 0%, transparent 70%), #0a1f1a',
      }}
      data-testid="clap-preview-page"
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
          <div>
            <p className="text-emerald-300 text-xs font-bold tracking-[0.3em] uppercase mb-1">Choose your celebration</p>
            <h1 className="text-3xl sm:text-4xl font-black text-white">Clapping Hand Variants</h1>
            <p className="text-white/60 mt-2 max-w-xl text-sm">
              Six animated treatments to replace the <em>GOAL!</em> badge on the success page.
              Tell me which number you like and I'll wire it in (or mix &amp; match — happy to combine).
            </p>
          </div>
          <button
            onClick={replayAll}
            className="px-4 py-2 rounded-xl bg-[#007a33] hover:bg-[#005c26] text-white text-sm font-bold transition-colors"
            data-testid="clap-replay-btn"
          >
            Replay all
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {variants.map(v => (
            <div
              key={v.id}
              className="clap-tile clap-tile-replay relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 flex flex-col items-center justify-end overflow-hidden"
              style={{ minHeight: '260px' }}
              data-testid={`clap-tile-${v.id}`}
            >
              <div className="flex-1 w-full flex items-center justify-center mb-4">
                {v.element}
              </div>
              <p className="text-white text-sm font-semibold">{v.title}</p>
              <p className="text-white/50 text-xs mt-0.5 text-center">{v.subtitle}</p>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-white/50 text-xs">
          Tap <strong className="text-white">Replay all</strong> to see them again. Animations loop every ~3.5 seconds.
        </p>
      </div>
    </div>
  );
}

function SvgHandClap() {
  // Deprecated — kept for reference. Variant 5 now uses Halo Pulse via JSX inline.
  return null;
}
